// --- Dependencies -------------------------------------------------------------------------------
import { useMemo, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useResourceTable from "@/hooks/useResourceTable";
import useTranslation from "@/hooks/useTranslation";
import type { TyContactRow, TyContactStatus } from "@/types";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import DataTable from "@/UI/components/DataTable";
import type { TyColumn } from "@/UI/components/DataTable/types";
import ListScreen from "@/UI/components/ListScreen";
import StatsSection from "@/UI/components/StatsSection";
import FilterSelect from "@/UI/inputs/FilterSelect";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import ContactPopup from "@/UI/popups/ContactPopup";
import { formatDate, formatNumber } from "@/utils/format";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type TyStats = { total: number; new: number; read: number; replied: number };

// --- Constants ----------------------------------------------------------------------------------

const STATUSES: TyContactStatus[] = ["new", "read", "replied"];

/** Characters of the message the table shows; the rest is the popup's job. */
const MESSAGE_PREVIEW = 38;

// --- Logic --------------------------------------------------------------------------------------

/**
 * Messages from the marketing site's contact form.
 *
 * Nobody here has an account, so there is no client or store to link to and the contact details are
 * the row — a request we cannot answer is worthless, which is why they sit in their own column next
 * to the name rather than behind a detail view.
 */
export default function ContactsScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { canEdit } = usePermission();
    const { addComp, removeComp } = usePopup();

    const [tab, setTab] = useState("");
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    /** Set from the repeat badge in the popup: everything one address or number ever sent. */
    const [channel, setChannel] = useState<{ field: "email" | "phone"; value: string } | null>(null);

    const table = useResourceTable<TyContactRow, TyStats>({
        path: "contacts",
        rowsKey: "contacts",
        query: {
            search,
            status: status || tab,
            email: channel?.field === "email" ? channel.value : "",
            phone: channel?.field === "phone" ? channel.value : "",
        },
    });

    const isEditable = canEdit("contacts");

    const stats = useMemo(() => {
        if (!table.stats) return [];
        return [
            { key: "total", label: t.contacts.statTotal, value: formatNumber(table.stats.total, lang), tone: "neutral" as const },
            { key: "new", label: t.contacts.statNew, value: formatNumber(table.stats.new, lang), tone: "brand" as const },
            { key: "read", label: t.contacts.statRead, value: formatNumber(table.stats.read, lang), tone: "warn" as const },
            { key: "replied", label: t.contacts.statReplied, value: formatNumber(table.stats.replied, lang), tone: "good" as const },
        ];
    }, [table.stats, t, lang]);

    const columns = useMemo<TyColumn<TyContactRow>[]>(
        () => [
            {
                key: "name",
                label: t.cols.name,
                width: "minmax(140px, 1fr)",
                render: (row) => <span className="list-cell-strong">{row.name}</span>,
            },
            {
                key: "status",
                label: t.cols.status,
                width: "140px",
                render: (row) => <span className={`contacts-status contacts-status--${row.status}`}>{t.enums.contactStatus[row.status]}</span>,
                edit: isEditable
                    ? {
                          kind: "select",
                          options: STATUSES.map((one) => ({ value: one, label: t.enums.contactStatus[one] })),
                          value: (row) => row.status,
                      }
                    : undefined,
            },
            {
                /* A column each, so a row carrying only one channel still reads under the right
                   heading — scanning for "who left a number" is the whole point of splitting them. */
                key: "email",
                label: t.contacts.colEmail,
                width: "minmax(170px, 1fr)",
                render: (row) =>
                    row.email ? (
                        <a href={`mailto:${row.email}`} className="contacts-channel" dir="ltr" onClick={(e) => e.stopPropagation()}>
                            {row.email}
                        </a>
                    ) : (
                        <span className="list-cell-muted">{t.contacts.noContact}</span>
                    ),
            },
            {
                key: "phone",
                label: t.contacts.colPhone,
                width: "150px",
                render: (row) =>
                    row.phone ? (
                        <a
                            href={`https://wa.me/${row.phone.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="contacts-channel"
                            dir="ltr"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {row.phone}
                        </a>
                    ) : (
                        <span className="list-cell-muted">{t.contacts.noContact}</span>
                    ),
            },
            {
                key: "message",
                label: t.contacts.colMessage,
                width: "260px",
                /* Cut in JS, not just by CSS: the cell is a grid track, and a 2000-character
                   message would stretch the whole table before `text-overflow` ever applied. */
                render: (row) => (
                    <button type="button" className="contacts-message" onClick={() => openContact(row)}>
                        {row.message.length > MESSAGE_PREVIEW ? `${row.message.slice(0, MESSAGE_PREVIEW).trimEnd()}...` : row.message}
                    </button>
                ),
            },
            {
                key: "createdAt",
                label: t.cols.created,
                width: "130px",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{formatDate(row.createdAt, lang)}</span>,
            },
        ],
        [t, lang, isEditable]
    );

    const openContact = (row: TyContactRow) => {
        removeComp("contact-preview");
        addComp({
            name: "contact-preview",
            Component: ContactPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                contact: row,
                // Reuses the table's own write path, so the list and the stats refresh with it.
                onStatus: isEditable ? (next: TyContactStatus) => table.saveCell(row._id, "status", next) : undefined,
                onFilter: (field: "email" | "phone", value: string) => {
                    setChannel({ field, value });
                    removeComp("contact-preview");
                },
            },
        });
    };

    const confirmDelete = (ids: string[], label: string) => {
        removeComp("confirm-delete-contact");
        addComp({
            name: "confirm-delete-contact",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                message: `${t.contacts.deleteWarning} — ${label}`,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.table.deleteConfirm,
                cancelLabel: t.table.deleteCancel,
                onConfirm: () => table.deleteBulk(ids),
            },
        });
    };

    return (
        <ListScreen writeError={table.writeError}>
            <StatsSection loading={table.loading && !table.stats} stats={stats} />

            <ActionsBar
                tabs={[
                    { value: "", label: t.contacts.tabAll },
                    { value: "new", label: t.contacts.tabNew },
                ]}
                tab={tab}
                onTab={setTab}
                search={{ value: search, onChange: setSearch, placeholder: t.contacts.search }}
                activeFilterCount={status ? 1 : 0}
                onClearFilters={() => setStatus("")}
                filters={
                    <FilterSelect
                        label={t.cols.status}
                        value={status}
                        onChange={setStatus}
                        options={STATUSES.map((one) => ({ value: one, label: t.enums.contactStatus[one] }))}
                    />
                }
            />

            {channel && (
                <div className="contacts-channel-filter">
                    <span>{t.contacts.filteredBy}</span>
                    <strong dir="ltr">{channel.value}</strong>
                    <button type="button" onClick={() => setChannel(null)} aria-label={t.contacts.clearChannel}>
                        <img src="/icons/close-icon.svg" alt="" width={11} height={11} />
                    </button>
                </div>
            )}

            <DataTable<TyContactRow>
                rows={table.rows}
                columns={columns}
                rowId={(row) => row._id}
                loading={table.loading}
                error={table.error}
                onRetry={table.refetch}
                pager={{ page: table.page, lastPage: table.lastPage, total: table.total, onPage: table.setPage }}
                selection={isEditable ? { selected: table.selected, onChange: table.setSelected } : undefined}
                onCellSave={isEditable ? (row, key, value) => table.saveCell(row._id, key, value) : undefined}
                rowActions={(row) => (
                    <>
                        <button type="button" className="list-row-btn" onClick={() => openContact(row)} aria-label={t.contacts.view}>
                            <img src="/icons/eye-opened-icon.svg" alt="" width={15} height={15} />
                        </button>
                        {isEditable && (
                            <button
                                type="button"
                                className="list-row-btn list-row-btn--danger"
                                onClick={() => confirmDelete([row._id], row.name)}
                                aria-label={t.table.deleteRow}
                            >
                                <img src="/icons/delete-icon.svg" alt="" width={15} height={15} />
                            </button>
                        )}
                    </>
                )}
            />

            {isEditable && (
                <BulkBar
                    count={table.selected.length}
                    busy={table.busy}
                    fields={[
                        {
                            key: "status",
                            label: t.cols.status,
                            kind: "select",
                            options: STATUSES.map((one) => ({ value: one, label: t.enums.contactStatus[one] })),
                        },
                    ]}
                    onApply={table.applyBulk}
                    onDelete={() => confirmDelete(table.selected, `${table.selected.length}`)}
                    onClear={() => table.setSelected([])}
                />
            )}
        </ListScreen>
    );
}
